import type {
  ConvertTablePaginationProps,
  ConvertTableParamsProps,
  ConvertTableSorterToListSortProps,
  FilterListTableColumns,
  ListTableColumn,
  ListTableFilters,
  ListTableSortItem,
} from "./list-table.type";
import type {
  TableViewFilters,
  TableViewFilterValue,
  TableViewSort,
  TableViewSortItem,
} from "@/entities/table-view";
import type { UserEntity } from "@/entities/user";
import type { ListItem, ListPagination, ListParams } from "@/modules/list";
import type { TableColumnType } from "antd";
import type { Key } from "react";

import omitBy from "lodash/omitBy";
import startsWith from "lodash/startsWith";

import { isDefined } from "@/utilities/defined";
import { isValidObject } from "@/utilities/object";

export const getListTableColumnKey = (columnId?: Key | Key[]): string => {
  if (!columnId) return "";

  if (typeof columnId === "string") return columnId;

  if (typeof columnId === "number") return `${columnId}`;

  if (Array.isArray(columnId)) return columnId.join(".");

  return "";
};

export const getListTableFilterColumnKey = <Item extends ListItem = ListItem>(
  column: ListTableColumn<Item>
) => {
  const { dataIndex, key } = column ?? {};
  return getListTableColumnKey(key ?? dataIndex);
};

export const getListTableSortColumnKey = <Item extends ListItem = ListItem>(
  column?: ListTableColumn<Item>,
  fallbackValue?: Key | Key[]
) => {
  const { dataIndex, sorterKey, key } = column ?? {};
  return getListTableColumnKey(sorterKey ?? key ?? dataIndex ?? fallbackValue);
};

export const filterListTableColumns = <Item extends ListItem = ListItem>({
  columns,
  hiddenColumns = [],
}: FilterListTableColumns<Item>) => {
  return columns.filter((column) => {
    const columnKey = getListTableFilterColumnKey(column);
    return !hiddenColumns.includes(columnKey);
  });
};

export const convertTableSorterToListSort = <Item extends ListItem = ListItem>({
  newTableSorter,
  currentListSort,
  defaultSort,
}: ConvertTableSorterToListSortProps<Item>): TableViewSort => {
  // `tableSorter` can be an object or an array of objects
  const tableSorterArray = Array.isArray(newTableSorter)
    ? newTableSorter
    : [newTableSorter];

  // Convert `tableSorter` into a `listSort` format (array of strings)
  const newListSort = tableSorterArray
    .map(convertTableSorterItemToListSortItem)
    .filter(isDefined);

  if (!newListSort.length) return defaultSort;

  // Re-order `listSort` to fix Ant Design sort params order bug when
  // using onChange => https://github.com/ant-design/ant-design/issues/40571
  // TODO: When Ant Design bug will be fixed, remove this part of code
  const finalListSort: TableViewSort = newListSort
    .map((sortItem) => {
      const currentListIndex = findSortItemIndex(sortItem, currentListSort);
      const index = currentListIndex !== -1 ? currentListIndex : 99999; // If not already included in currentListSort, put it at the end
      return { value: sortItem, index };
    })
    .sort((sortItemA, sortItemB) => sortItemA.index - sortItemB.index) // sort by index
    .map(({ value }) => value); // remove index

  return finalListSort;
};

export const convertTableSorterItemToListSortItem = <
  Item extends ListItem = ListItem,
>(
  tableSorterItem: ListTableSortItem<Item>
): TableViewSortItem | undefined => {
  const sortColumnKey = getListTableSorterItemColumnKey(tableSorterItem);

  if (!sortColumnKey || !tableSorterItem?.order) return undefined;

  const sortPrefix = tableSorterItem?.order === "descend" ? "-" : "";

  return `${sortPrefix}${sortColumnKey}`;
};

export const getListTableSorterItemColumnKey = <
  Item extends ListItem = ListItem,
>(
  tableSorterItem: ListTableSortItem<Item>
) => {
  // Ant Design table return multiple format for sort values. If `column`
  // is defined use it. Otherwise, build the column using `columnKey`
  // or `field`.
  const tableSortColumn = tableSorterItem?.column;
  const fallbackValue = tableSorterItem?.columnKey ?? tableSorterItem?.field;

  return getListTableSortColumnKey(tableSortColumn, fallbackValue);
};

export const findSortItemIndex = (
  sortItem: TableViewSortItem,
  sortList: TableViewSort = []
): number => {
  const cleanKey = sortItem.startsWith("-") ? sortItem.slice(1) : sortItem;

  const ascIndex = sortList?.indexOf(cleanKey);
  if (ascIndex !== -1) return ascIndex;

  const descIndex = sortList?.indexOf(`-${cleanKey}`);
  if (descIndex !== -1) return descIndex;

  return -1;
};

export const convertTableFiltersToListFilters = (
  tableFilters: ListTableFilters
): TableViewFilters => {
  const filters: TableViewFilters = {};

  Object.entries(tableFilters).forEach(([filterKey, filterValue]) => {
    // Ant Design table always return all the filter key with null value
    // by default. Let's clean-up filters here and only defined ones.
    if (filterValue) {
      const values = filterValue.map(convertTableFilterItem).filter(isDefined);
      filters[filterKey] = values;
    }
  });

  return filters;
};

export const convertTableFilterItem = (
  filterItem?: string | number | boolean | bigint
): TableViewFilterValue | undefined => {
  // Ant Design table always return all the filter key with null value
  // by default. Let's clean-up filters here and only defined ones.
  if (typeof filterItem === "number") return filterItem;
  if (typeof filterItem === "string") return filterItem;
  return undefined;
};

export const convertTablePaginationToListPagination = ({
  tablePagination,
  defaultPageSize,
}: ConvertTablePaginationProps): ListPagination => {
  return {
    current: tablePagination.current ?? 1,
    pageSize: tablePagination.pageSize ?? defaultPageSize,
  };
};

export const convertTableParamsToListParams = <
  Item extends ListItem = ListItem,
>({
  tableSort,
  tableFilters,
  tablePagination,
  currentListSort,
  defaultSort,
  defaultPageSize,
}: ConvertTableParamsProps<Item>): ListParams => {
  return {
    sort: convertTableSorterToListSort({
      newTableSorter: tableSort,
      currentListSort,
      defaultSort,
    }),
    filters: convertTableFiltersToListFilters(tableFilters),
    pagination: convertTablePaginationToListPagination({
      tablePagination,
      defaultPageSize,
    }),
  };
};

export const userCanFilterListTableColumn = <Item extends ListItem = ListItem>({
  user,
  column,
}: {
  user?: UserEntity;
  column: ListTableColumn<Item>;
}) => {
  if (!column.filtersIfCan) return true;

  return user?._authorizedActions?.includes(column.filtersIfCan);
};

export const getUserListTableColumn = <Item extends ListItem = ListItem>(
  column: ListTableColumn<Item>,
  user?: UserEntity
): ListTableColumn<Item> => {
  const canFilter = userCanFilterListTableColumn({ user, column });

  if (canFilter) return column;

  return omitBy(column, (_, key) =>
    startsWith(key, "filter", 0)
  ) as ListTableColumn<Item>;
};

export const getColumnFilterInfos = <Item extends ListItem = ListItem>(
  column: ListTableColumn<Item>,
  filters: TableViewFilters
) => {
  const filterKey = getListTableFilterColumnKey(column);

  // AntD log errors when filteredValue is used only for some columns
  // because it means the filters state of the table is now controled by
  // the props but not every columns is handling it. In this case, we
  // need to keep track of the table filters returned by AntD. And use
  // it to define filteredValue for each column. Always fallback to null
  // if the column do not have a corresponding filteredValue.
  const filteredValue = column.filteredValue ?? filters[filterKey] ?? null;

  return {
    filterKey,
    filteredValue,
  };
};

export const getColumnSortInfos = <Item extends ListItem = ListItem>(
  column: ListTableColumn<Item>,
  sort: TableViewSort,
  multipleSort?: boolean
) => {
  const sortKey = getListTableSortColumnKey(column);

  // Force multiple sortable for all sortable columns
  let sorter = column.sorter;
  if (multipleSort && sorter) {
    const existingValueFormatted = isValidObject(sorter) ? sorter : {}; // handle `sorter: true` & `sorter: { compare: fn() }`
    sorter = { ...existingValueFormatted, multiple: 1 };
  }

  // Make sure the sorted column is highligted on ListTable
  const sortOrder: TableColumnType["sortOrder"] = sort.includes(sortKey)
    ? "ascend"
    : sort.includes(`-${sortKey}`)
      ? "descend"
      : undefined;

  return {
    sorter,
    sortKey,
    sortOrder,
  };
};
